import { NavLink } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { NAV_ITEMS, SECTION_LABEL_KEYS, type NavDef } from "@/config/navigation";
import { NAV_ICONS } from "@/layouts/navIcons";
import { YouTubeNavIcon } from "@/layouts/YouTubeNavIcon";
import { useUiStore } from "@/store/uiStore";
import { cn } from "@/utils/cn";

function groupBySection(items: NavDef[]) {
  const order: NavDef["section"][] = ["main", "create", "optimize", "system"];
  return order.map((section) => ({
    section,
    labelKey: SECTION_LABEL_KEYS[section],
    items: items.filter((i) => i.section === section),
  }));
}

export function Sidebar() {
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const t = useUiStore((s) => s.t);
  const locale = useUiStore((s) => s.locale);
  const groups = groupBySection(NAV_ITEMS);

  return (
    <aside
      className={cn(
        "flex h-full flex-col border-r border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-950",
        collapsed ? "w-[72px]" : "w-[260px]",
      )}
    >
      <div className={cn("flex h-14 items-center border-b border-zinc-200 px-4 dark:border-zinc-800", collapsed && "justify-center px-2")}>
        <BrandLogo
          className={cn(
            "shrink-0 rounded-lg",
            collapsed ? "h-8 w-8 object-cover object-left" : "h-8 w-auto",
          )}
        />
        {!collapsed ? (
          <div className="ml-2.5 min-w-0">
            <div className="truncate text-[15px] font-bold tracking-tight bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent">
              {t("app.name")}
            </div>
            <div className="truncate text-[10px] font-semibold uppercase tracking-wider text-indigo-500/80 dark:text-indigo-400/80">
              {t("app.shortName")}
            </div>
          </div>
        ) : null}
      </div>

      <nav
        className="flex-1 overflow-y-auto px-2 py-3 [scrollbar-width:thin] [scrollbar-color:rgba(156,163,175,0.4)_transparent] dark:[scrollbar-color:rgba(113,113,122,0.4)_transparent]"
        key={locale}
      >
        {groups.map((group) => (
          <div key={group.section} className="mb-4">
            {!collapsed ? (
              <div className="mb-1.5 px-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-400">
                {t(group.labelKey)}
              </div>
            ) : null}
            <ul className="space-y-0.5">
              {group.items.map((item) => {
                const Icon = NAV_ICONS[item.icon];
                const label = t(item.labelKey);
                return (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      end={item.path === "/"}
                      title={label}
                      className={({ isActive }) =>
                        cn(
                          "flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors",
                          collapsed && "justify-center px-2",
                          isActive
                            ? "bg-[var(--amos-brand)] !text-white shadow-sm [&_svg]:!text-white [&_svg]:stroke-white"
                            : "text-zinc-700 hover:bg-zinc-100 hover:text-zinc-950 dark:text-zinc-400 dark:hover:bg-zinc-900 dark:hover:text-zinc-100",
                        )
                      }
                    >
                      {item.brandIcon === "youtube" ? (
                        <YouTubeNavIcon />
                      ) : (
                        <Icon className="h-4 w-4 shrink-0" strokeWidth={2} />
                      )}
                      {!collapsed ? <span className="truncate">{label}</span> : null}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      <div className={cn("border-t border-zinc-200 p-2 dark:border-zinc-800", collapsed && "flex justify-center")}>
        <NavLink
          to="/privacy"
          target="_blank"
          title="Privacy Policy & Trust"
          className={cn(
            "flex items-center gap-2.5 rounded-md px-3 py-2 text-xs font-medium text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-900 dark:hover:text-zinc-100",
            collapsed && "justify-center px-2",
          )}
        >
          <ShieldCheck className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
          {!collapsed ? <span className="truncate">Privacy Policy</span> : null}
        </NavLink>
      </div>
    </aside>
  );
}
